import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { LucideAngularModule, Check, Loader2 } from 'lucide-angular';

export interface LoadingStep {
  id: number;
  label: string;
}

/** Default theme color (brand orange) */
const DEFAULT_THEME_COLOR = '#f56600';

/** Exponential decay factor - controls how fast progress approaches 100% */
const DECAY_FACTOR = 3;

@Component({
  selector: 'app-loading-progress',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './loading-progress.html',
  styleUrl: './loading-progress.scss',
})
export class LoadingProgressComponent implements OnInit, OnDestroy {
  /** Title displayed at the top */
  @Input() title = 'Processing...';

  /** Subtitle/description displayed below the title */
  @Input() subtitle = "We're working on it. This won't take long!";

  /** Tip text displayed at the bottom */
  @Input() tipText = 'Please wait while we process your request.';

  /** Theme color for progress bar and active step */
  @Input() themeColor = DEFAULT_THEME_COLOR;

  /** Target duration for exponential progress (in ms) - defaults to 60 seconds */
  @Input() targetDuration = 60000;

  /** Loading steps to display */
  @Input() steps: LoadingStep[] = [];

  protected readonly icons = { Check, Loader2 };

  /** Current progress (0-100) */
  private readonly progress$$ = new BehaviorSubject<number>(0);
  protected readonly progress$ = this.progress$$.asObservable();

  /** Current loading step index */
  private readonly currentStepIndex$$ = new BehaviorSubject<number>(0);
  protected readonly currentStepIndex$ = this.currentStepIndex$$.asObservable();

  /** Start time for progress calculation */
  private startTime = 0;

  /** Animation frame ID for cleanup */
  private animationFrameId: number | null = null;

  /** Flag to stop animation loop */
  private isRunning = false;

  /** Last update timestamp to throttle updates */
  private lastUpdateTime = 0;

  /** Last emitted step index to avoid unnecessary updates */
  private lastStepIndex = -1;

  /** Minimum time between updates in ms */
  private readonly UPDATE_THROTTLE = 100;

  ngOnInit(): void {
    this.startTime = Date.now();
    this.isRunning = true;
    this.animationLoop();
  }

  ngOnDestroy(): void {
    this.stopProgressUpdate();
  }

  /**
   * Animation loop using requestAnimationFrame
   * This won't be throttled like setInterval when the tab is visible
   */
  private animationLoop(): void {
    if (!this.isRunning) return;

    const now = Date.now();
    const elapsed = now - this.startTime;

    // Throttle updates to every 100ms for performance
    if (now - this.lastUpdateTime >= this.UPDATE_THROTTLE) {
      this.lastUpdateTime = now;

      // Calculate exponential progress: 100 * (1 - e^(-kt))
      const k = DECAY_FACTOR / this.targetDuration;
      const rawProgress = 100 * (1 - Math.exp(-k * elapsed));
      const progress = Math.min(rawProgress, 99);

      // Update progress with fractional value for smooth CSS transition
      this.progress$$.next(progress);

      // Update step index based on progress thresholds
      if (this.steps.length > 0) {
        const progressPerStep = 100 / this.steps.length;
        const stepIndex = Math.min(
          Math.floor(progress / progressPerStep),
          this.steps.length - 1
        );
        if (stepIndex !== this.lastStepIndex) {
          this.lastStepIndex = stepIndex;
          this.currentStepIndex$$.next(stepIndex);
        }
      }
    }

    // Continue animation loop
    this.animationFrameId = requestAnimationFrame(() => this.animationLoop());
  }

  /**
   * Stop progress updates
   */
  private stopProgressUpdate(): void {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Complete the progress (called externally when processing is done).
   * Sets progress to 100% and marks all steps as completed.
   */
  complete(): void {
    this.stopProgressUpdate();
    this.progress$$.next(100);
    this.currentStepIndex$$.next(this.steps.length);
  }
}
